import { useState } from 'react'
import {
  Box, Button, Card, Typography, Table, TableBody, TableCell, TableHead,
  TableRow, Tooltip, Chip,
} from '@mui/material'
import { ExpandMore, ExpandLess } from '@mui/icons-material'
import {
  UTIL_BANDS, utilBand, lineUtilization, overallUtilization, grossHoursForMonth, dosRiskPct,
  noRunRateCount, riskBarFill,
} from './fourToTwelve'
import { monthHeading } from './nextThreeMonths'
import { frozenWidth, monthWidth, tableWidth, MONTH_TINT, TINT_ON, MONTH_DIVIDER, CAPACITY_CARD_SX } from './gridLayout'

// FAR-88 / FAR-90 S16: the mockup's amber and red (the theme has none).
const AMBER = { fill: '#FCF4CC', border: '#E5CE74', text: '#6B5410' }
const RED_TEXT = '#8E3326'
const RED_BAR = '#C0533F'
const AMBER_BAR = '#D9B23F'

// FAR-88 C4 / S5: a heat chip fills its month cell (10px each side), about
// 26px tall, 6px radius, the band's fill, the value bold and centred.
function HeatChip({ value }) {
  const band = utilBand(value)
  return (
    <Box data-band={band?.key || 'none'} sx={{
      height: 26, borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center',
      bgcolor: band?.color || 'transparent', color: band?.text || 'text.secondary', fontWeight: 700, fontSize: '0.8125rem',
    }}>
      {pct(value)}
    </Box>
  )
}

// FAR-88 S4: month name bold; "rest of month" muted and small beside it.
function MonthHead({ month, index, basePlan }) {
  const text = monthHeading(month, index, basePlan)
  const partial = text !== month.label
  return (
    <>
      {month.label}
      {partial && <Box component="span" sx={{ ml: 1, fontWeight: 400, fontSize: '0.7rem', color: 'text.secondary' }}>rest of month</Box>}
    </>
  )
}

const LINES = ['LINE 1', 'LINE 2', 'LINE 3', 'LINE 4']
const SLATE = '#475569'

const pct = (v) => (v == null ? '—' : `${Math.round(v)}%`)

// Capacity and Risk. Collapsible (expanded by default).
// - Next 3 Months (FAR-77, the default): a row per line; clicking one filters
//   the SKU Plan to that primary line.
// - Months 4–18 (FAR-83 §3, overall): one Overall Capacity row across all
//   lines; no Warehouse row; no row is clickable.
// Month columns alternate a light blue tint (FAR-77/83 §2), header included.
//
// FAR-83 §3 / FAR-77 §3 and the mockup: the header row holds Hide/Show at the
// left, the title, the compact legend, and the month headers. Hide collapses
// the body; the header row (with the months) stays.
const TINT = MONTH_TINT // FAR-88 S7, shared with the SKU grid
const HEAD = '#EEF1F4' // FAR-88 C5 / S16 slate tint
const HEAD_TINT = TINT_ON.slate // header row on a tinted month (solid: it is the header)
const tint = (i) => (i % 2 === 1 ? { bgcolor: TINT } : {})
// FAR-83 #13: each month cell starts with the SKU grid's 2px month border.
const monthCell = (i) => ({ ...MONTH_DIVIDER, ...tint(i) })
// The legend's compact labels, as in the mockup: <80, 80–90, 90–100, >100.
const LEGEND = { under: '<80', good: '80–90', caution: '90–100', over: '>100' }

export default function CapacityRiskCard({
  rows, months, capacity, grossHours, activeLine, onLineClick, overall = false, scrollRef, basePlan, withSelect = true,
}) {
  // withSelect false: the SKU grid beside it has no checkbox column (the Base
  // Plan tab, FAR-89 #1), so the label column is narrower to match.
  const excluded = noRunRateCount(rows)
  // grossHours: H, line → month (yyyy-mm) → hours, as GET /capacity/lines
  // serves it (FAR-94). Never recomputed here.
  const [open, setOpen] = useState(true)
  const capByLine = Object.fromEntries((capacity?.lines || []).map((l) => [l.line, l]))
  // FAR-83 #13: the label column is as wide as the SKU grid's frozen columns
  // and each month as wide as its four sub-columns, so the months line up.
  const withLines = !overall
  // Sticky label cell; on the active line row it keeps the row's highlight.
  // action.selected is see-through, so it is layered over a solid paper
  // background; otherwise months would show through when scrolled under it.
  const label = (active = false) => ({
    textAlign: 'left !important', fontWeight: 600, position: 'sticky', left: 0, zIndex: 1, bgcolor: 'background.paper',
    ...(active && { backgroundImage: (t) => `linear-gradient(${t.palette.action.selected}, ${t.palette.action.selected})` }),
  })

  return (
    // Plain, not outlined: an outlined card's 1px right border would make it
    // 1px narrower inside than the SKU card.
    <Card sx={CAPACITY_CARD_SX} data-testid="capacity-risk-card">
      {/* Scrolls with the SKU grid (useSyncedScrollX). The stable gutter
          matches the grid's, so both reach the same right end. */}
      <Box ref={scrollRef} sx={{ overflowX: 'auto', scrollbarGutter: 'stable' }}>
        <Table size="small" sx={{
          tableLayout: 'fixed', borderCollapse: 'separate', borderSpacing: 0, width: tableWidth(withLines, months.length, withSelect),
          '& td, & th': { textAlign: 'center', py: 0.5, overflow: 'hidden' },
        }}>
          <colgroup>
            <col style={{ width: frozenWidth(withLines, withSelect) }} />
            {months.map((m) => <col key={m.key} style={{ width: monthWidth() }} />)}
          </colgroup>
          <TableHead>
            <TableRow data-testid="capacity-header">
              <TableCell sx={{ ...label(), bgcolor: HEAD, py: '6px !important' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
                  <Button size="small" variant="outlined" color="inherit" onClick={() => setOpen((v) => !v)}
                    startIcon={open ? <ExpandLess /> : <ExpandMore />} aria-expanded={open}
                    aria-label={open ? 'Hide Capacity and Risk' : 'Show Capacity and Risk'}
                    sx={{ borderRadius: 4, py: 0, px: 1, minWidth: 0, textTransform: 'none', fontSize: '0.75rem', bgcolor: 'background.paper', borderColor: 'divider' }}>
                    {open ? 'Hide' : 'Show'}
                  </Button>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: SLATE }}>
                    Capacity and Risk
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }} aria-label="Utilization legend" data-testid="capacity-legend">
                    {UTIL_BANDS.map((b) => (
                      <Tooltip key={b.key} title={b.label}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                          <Box sx={{ width: 10, height: 10, bgcolor: b.color, borderRadius: '2px' /* S4: rounded squares */ }} />
                          <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>{LEGEND[b.key] || b.label}</Typography>
                        </Box>
                      </Tooltip>
                    ))}
                  </Box>
                  {capacity?.placeholder && (
                    <Tooltip title="Line hours are placeholder values, not Faribault's. Set real hours in Admin → Capacity.">
                      <Chip /* FAR-90 S4: amber, as the other warnings (not an orange outline). */ label="Placeholder capacity" size="small" data-testid="placeholder-capacity"
                        sx={{ height: 20, fontSize: '0.65rem', bgcolor: AMBER.fill, color: AMBER.text, border: `1px solid ${AMBER.border}` }} />
                    </Tooltip>
                  )}
                </Box>
              </TableCell>
              {months.map((m, i) => (
                <TableCell key={m.key} data-month-head={m.key}
                  sx={{ fontWeight: 700, ...monthCell(i), bgcolor: i % 2 === 1 ? HEAD_TINT : HEAD }}>
                  <MonthHead month={m} index={i} basePlan={basePlan} />
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          {open && (
            <TableBody data-testid="capacity-body">
              {overall && (
                <TableRow data-testid="overall-capacity-row">
                  <TableCell sx={label()}>Overall Capacity</TableCell>
                  {months.map((m, i) => {
                    const v = overallUtilization(rows, m.key, capacity?.lines, grossHoursForMonth(grossHours, m.iso))
                    return (
                      <TableCell key={m.key} sx={{ ...monthCell(i), px: '10px' }}>
                        <HeatChip value={v} />
                      </TableCell>
                    )
                  })}
                </TableRow>
              )}
              {!overall && LINES.map((line) => {
                const active = activeLine === line
                return (
                  <TableRow key={line} hover onClick={() => onLineClick?.(active ? '' : line)}
                    sx={{ cursor: 'pointer', bgcolor: active ? 'action.selected' : undefined }}
                    aria-selected={active}>
                    <TableCell sx={label(active)}>
                      {line.replace('LINE', 'Line')}
                    </TableCell>
                    {months.map((m, i) => {
                      const v = lineUtilization(rows, line, m.key, capByLine[line], grossHours?.[line]?.[m.iso])
                      return (
                        <TableCell key={m.key} sx={{ ...monthCell(i), px: '10px' }}>
                          <HeatChip value={v} />
                        </TableCell>
                      )
                    })}
                  </TableRow>
                )
              })}
              {/* FAR-88 S6: bold label; the value left-aligned above the bar in
                  red / dark amber; a 6px rounded bar on a gray track, where
                  15% fills the track. */}
              {[['below', '% Below Min DOS', RED_TEXT, RED_BAR], ['above', '% Above Max DOS', AMBER.text, AMBER_BAR]].map(([which, text, color, bar]) => (
                <TableRow key={which} data-testid={`risk-${which}`}>
                  <TableCell sx={{ ...label(), fontWeight: 700 }}>{text}</TableCell>
                  {months.map((m, i) => {
                    const v = dosRiskPct(rows, m.key, which)
                    return (
                      <TableCell key={m.key} sx={{ ...monthCell(i), px: '10px', textAlign: 'left !important' }}>
                        <Box sx={{ fontWeight: 600, fontSize: '0.75rem', color, lineHeight: 1.4 }}>{pct(v)}</Box>
                        <Box sx={{ height: 6, bgcolor: '#E6EAE4', borderRadius: 3, mt: 0.25 }}>
                          <Box data-fill={riskBarFill(v)} sx={{ height: 6, width: `${riskBarFill(v)}%`, bgcolor: bar, borderRadius: 3 }} />
                        </Box>
                      </TableCell>
                    )
                  })}
                </TableRow>
              ))}
              {/* FAR-88 / FAR-90 C6: what the figures leave out. */}
              {excluded > 0 && (
                <TableRow data-testid="capacity-footnote">
                  <TableCell sx={{ ...label(), fontWeight: 400, py: '4px !important' }}>
                    <Typography variant="caption" sx={{ color: AMBER.text }}>
                      Excludes {excluded} SKU{excluded === 1 ? '' : 's'} with no run rate
                    </Typography>
                  </TableCell>
                  {months.map((m, i) => <TableCell key={m.key} sx={monthCell(i)} />)}
                </TableRow>
              )}
            </TableBody>
          )}
        </Table>
      </Box>
    </Card>
  )
}
